import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { rmAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const OUTCOME_LABEL = {
  connected: 'Connected',
  no_answer: 'No answer',
  callback_later: 'Callback later',
  message_sent: 'Message sent',
  waiting_for_availability: 'Waiting for availability',
  ptm_scheduled: 'PTM scheduled',
};

const SENTIMENT_LABEL = {
  positive: 'Positive',
  negative: 'Negative',
  neutral: 'Neutral',
};

function sentimentChipColor(value) {
  if (value === 'positive') return 'success';
  if (value === 'negative') return 'error';
  return 'default';
}

function formatDate(value) {
  if (!value) return '—';
  return String(value).slice(0, 10);
}

export default function RmStudents() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isSalesCountryScope } = useAuth();
  const canAssign = isAdmin() || isSalesCountryScope();
  const [search, setSearch] = useState('');
  const [rmStatus, setRmStatus] = useState(canAssign ? 'unassigned' : '');
  const [rmUserId, setRmUserId] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [country, setCountry] = useState('');
  const [sentiment, setSentiment] = useState('');
  const [sortBy, setSortBy] = useState('follow_up_asc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [assignTo, setAssignTo] = useState('');

  const params = useMemo(
    () => ({
      search: search || undefined,
      rm_status: canAssign ? rmStatus || undefined : undefined,
      rm_user_id: canAssign ? rmUserId || undefined : undefined,
      follow_up: followUp || undefined,
      country: country || undefined,
      sentiment: sentiment || undefined,
      sort_by: sortBy || undefined,
      page,
      limit: 20,
    }),
    [search, rmStatus, rmUserId, followUp, country, sentiment, sortBy, page, canAssign]
  );

  const listQuery = useQuery(['rm-students', params], async () => {
    const res = await rmAPI.listStudents(params);
    return res.data;
  });

  const candidatesQuery = useQuery(
    ['rm-candidates'],
    async () => {
      const res = await rmAPI.getCandidates();
      return res.data?.data || [];
    },
    { enabled: canAssign }
  );

  const rows = listQuery.data?.data || [];
  const pagination = listQuery.data?.pagination || { page: 1, pages: 1, total: 0 };
  const countries = listQuery.data?.filters?.countries || [];
  const candidates = candidatesQuery.data || [];

  const assignMut = useMutation(
    ({ studentIds, rmUserId: nextRm }) =>
      studentIds.length === 1
        ? rmAPI.assign(studentIds[0], { rm_user_id: nextRm || null })
        : rmAPI.assignBulk({ student_ids: studentIds, rm_user_id: nextRm || null }),
    {
      onSuccess: () => {
        toast.success('RM assignment updated');
        setSelected([]);
        queryClient.invalidateQueries(['rm-students']);
        queryClient.invalidateQueries(['rm-candidates']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not assign RM'),
    }
  );

  const toggleRow = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        RM students
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {canAssign
          ? 'Assign a relationship manager, then open a student to see details, approved assessments, and parent-call notes.'
          : 'Your RM students. Open a student to see details, approved assessments, and log a parent call.'}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 220 }}
          />
          {canAssign ? (
            <TextField
              select
              size="small"
              label="RM status"
              value={rmStatus}
              onChange={(e) => {
                setRmStatus(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="unassigned">No RM</MenuItem>
              <MenuItem value="assigned">Has RM</MenuItem>
              <MenuItem value="mine">Assigned to me</MenuItem>
            </TextField>
          ) : null}
          {canAssign ? (
            <TextField
              select
              size="small"
              label="RM person"
              value={rmUserId}
              onChange={(e) => {
                setRmUserId(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Anyone</MenuItem>
              {candidates.map((c) => (
                <MenuItem key={c.userId} value={c.userId}>
                  {c.name} ({c.rmStudentCount})
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            select
            size="small"
            label="Follow-up"
            value={followUp}
            onChange={(e) => {
              setFollowUp(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="due">Due / overdue</MenuItem>
            <MenuItem value="upcoming">Upcoming</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Country"
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All countries</MenuItem>
            {countries.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
            {country && !countries.includes(country) ? <MenuItem value={country}>{country}</MenuItem> : null}
          </TextField>
          <TextField
            select
            size="small"
            label="Sentiment"
            value={sentiment}
            onChange={(e) => {
              setSentiment(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="positive">Positive</MenuItem>
            <MenuItem value="negative">Negative</MenuItem>
            <MenuItem value="neutral">Neutral</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Sort"
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="follow_up_asc">Follow-up: soonest first</MenuItem>
            <MenuItem value="follow_up_desc">Follow-up: latest first</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {canAssign && selected.length > 0 ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Typography variant="body2">{selected.length} selected</Typography>
            <TextField
              select
              size="small"
              label="Assign RM"
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">Unassign</MenuItem>
              {candidates.map((c) => (
                <MenuItem key={c.userId} value={c.userId}>
                  {c.name} · {c.role} · {c.rmStudentCount} students
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              disabled={assignMut.isLoading}
              onClick={() => assignMut.mutate({ studentIds: selected, rmUserId: assignTo })}
            >
              Apply
            </Button>
            <Button onClick={() => setSelected([])}>Clear</Button>
          </Stack>
        </Paper>
      ) : null}

      {listQuery.isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {canAssign ? <TableCell padding="checkbox" /> : null}
                <TableCell>Student</TableCell>
                <TableCell>Closer</TableCell>
                <TableCell>RM</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Last call</TableCell>
                <TableCell>Next follow-up</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/rm/${row.id}`)}
                >
                  {canAssign ? (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.includes(row.id)}
                        onChange={() => toggleRow(row.id)}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <Typography fontWeight={600}>
                      {row.first_name} {row.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.display_id} · {row.country || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.closer_name || '—'}</TableCell>
                  <TableCell>{row.rm_name || 'Unassigned'}</TableCell>
                  <TableCell>{row.batch_name || '—'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {row.last_outcome ? (
                        <Chip size="small" label={OUTCOME_LABEL[row.last_outcome] || row.last_outcome} />
                      ) : (
                        '—'
                      )}
                      {row.last_sentiment ? (
                        <Chip
                          size="small"
                          color={sentimentChipColor(row.last_sentiment)}
                          label={SENTIMENT_LABEL[row.last_sentiment] || row.last_sentiment}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(row.next_follow_up_date)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canAssign ? 7 : 6}>
                    <Typography color="text.secondary" align="center" py={3}>
                      No students found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pagination.pages > 1 ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={(_e, next) => setPage(next)}
          />
        </Box>
      ) : null}
    </Box>
  );
}
